/* Customer reviews: photo wall (sections/customer-reviews, layout "photo-wall"). RND #11.
 * - Masonry: the cards are server-rendered in one list, in section order. Each next
 *   visible card goes into the currently shortest column (4 columns at 990px+, 3 at
 *   750-989px, 2 below). Re-runs after images/fonts load, on resize and on filter change.
 * - Filter pills ("All reviews" / "With photos"), client-side, with aria-pressed.
 * - "Read more" appears only on quotes that are actually clamped.
 * - Mobile: the first `mobile_visible` cards, then "Show all N reviews".
 * - Popup: native <dialog> with the full review, prev/next through the currently
 *   filtered list, "N of M" counter, Esc / arrow keys, focus kept inside and
 *   returned to the card on close.
 * Review stream (layout "stream", [data-rw-flow]): the same wall, set in motion. The
 *   grid becomes a fixed-height window and each masonry column drifts upward in a
 *   loop: its cards are cloned once (aria-hidden, out of the tab order) and the CSS
 *   animation moves it by one full set (--rw-shift). Columns run at slightly
 *   different speeds and start out of step. No "Show all" while it flows. Reduced
 *   motion, or a column too short to fill the window: the plain wall. The pause
 *   button, going off screen and the open popup stop it; a click on a clone opens
 *   its original.
 * No Swiper. Review text is only ever copied from the server-rendered cards. */
(() => {
	if (window.ReviewsWall) return;

	const SELECTORS = {
		root: '[data-reviews-wall]',
		grid: '[data-rw-grid]',
		card: '[data-rw-card]',
		open: '[data-rw-open]',
		quote: '[data-rw-quote]',
		read: '[data-rw-read]',
		name: '[data-rw-name]',
		stars: '[data-rw-stars]',
		filter: '[data-rw-filter]',
		more: '[data-rw-more]',
		dialog: '[data-rw-dialog]',
		frame: '[data-rw-frame]',
		panel: '[data-rw-panel]',
		panelMedia: '[data-rw-panel-media]',
		panelText: '[data-rw-panel-text]',
		panelName: '[data-rw-panel-name]',
		panelStars: '[data-rw-panel-stars]',
		panelQuote: '[data-rw-panel-quote]',
		panelProduct: '[data-rw-panel-product]',
		panelThumb: '[data-rw-panel-thumb]',
		panelProductTitle: '[data-rw-panel-product-title]',
		panelLink: '[data-rw-panel-link]',
		prev: '[data-rw-prev]',
		next: '[data-rw-next]',
		close: '[data-rw-close]',
		counter: '[data-rw-counter]',
		toggle: '[data-rw-toggle]',
		clone: '[data-rw-clone]',
		pinnedCard: '[data-rw-pinned] [data-rw-card]',
	};

	// Review stream: px per second (the section's "Speed" sets data-speed); each column
	// runs at its own share of it and starts a few px in, so they never line up while
	// the first (photo) cards are still in view on arrival.
	const DEFAULT_FLOW_SPEED = 22;
	const FLOW_SPEED_FACTORS = [1, 0.86, 1.12, 0.94];
	const FLOW_START_PX = [0, 150, 70, 220];
	const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

	const PHOTO_MAX_SCALE = 1.5;
	const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
	const supportsDialog = typeof HTMLDialogElement === 'function' && 'showModal' in HTMLDialogElement.prototype;
	const desktopQuery = window.matchMedia('(min-width: 990px)');
	const tabletQuery = window.matchMedia('(min-width: 750px)');

	function columnCount() {
		if (desktopQuery.matches) return 4;
		if (tabletQuery.matches) return 3;
		return 2;
	}

	function isMobile() {
		return !tabletQuery.matches;
	}

	class Wall {
		constructor(root) {
			this.root = root;
			this.isFlow = root.hasAttribute('data-rw-flow');
			this.flowing = false;
			this.flowBlocked = false;
			this.grid = root.querySelector(SELECTORS.grid);
			this.cards = Array.from(this.grid.querySelectorAll(SELECTORS.card));
			// Review stream, product page: the product's own reviews, outside the wall.
			this.pinned = Array.from(root.querySelectorAll(SELECTORS.pinnedCard));
			this.moreButton = root.querySelector(SELECTORS.more);
			this.moreWrap = this.moreButton ? this.moreButton.parentElement : null;
			this.filters = Array.from(root.querySelectorAll(SELECTORS.filter));
			this.dialog = root.querySelector(SELECTORS.dialog);
			this.mobileVisible = parseInt(root.dataset.mobileVisible, 10) || 6;
			this.showAllLabel = root.dataset.showAllLabel || '';
			this.counterLabel = root.dataset.counterLabel || '';
			this.filter = 'all';
			this.expanded = false;
			this.columns = [];
			this.frame = null;
			this.current = -1;
			this.openedFrom = null;

			// Hidden cards are parked here so the columns only hold what is visible.
			this.stash = document.createElement('div');
			this.stash.className = 'reviews-wall__stash';
			this.stash.hidden = true;

			this.grid.classList.add('is-masonry');
			this.grid.after(this.stash);
			// The inline script in the snippet normally adds this; the theme editor doesn't
			// run inline scripts when it re-renders a section, so make sure it is there.
			root.classList.add('reviews-wall--js');
			root.setAttribute('data-js-ready', '');

			this.bindEvents();
			if (this.isFlow) this.setupFlow();
			this.update();
		}

		/* ---------- Which cards are visible ---------- */

		// The popup walks the pinned reviews first, then the (filtered) wall.
		popupCards() {
			return this.pinned.concat(this.filteredCards());
		}

		filteredCards() {
			if (this.filter === 'photos') return this.cards.filter((card) => card.dataset.hasPhoto === 'true');
			return this.cards;
		}

		update() {
			const filtered = this.filteredCards();
			this.flowing = this.isFlow && !reducedMotionQuery.matches && !this.flowBlocked;
			// A flowing wall shows every card in its window; "Show all" is for the still wall.
			const collapsed = !this.flowing && isMobile() && !this.expanded && filtered.length > this.mobileVisible;
			const visible = collapsed ? filtered.slice(0, this.mobileVisible) : filtered;
			this.visible = visible;

			if (this.moreWrap) {
				this.moreWrap.hidden = !collapsed;
				if (collapsed) this.moreButton.textContent = this.showAllLabel.replace('[count]', filtered.length);
			}

			this.layout();
			// Clones copy each card's "Read more" state, so decide it first.
			this.updateReadMore();
			this.updateReadMore(this.pinned);
			if (this.isFlow) this.applyFlow();
		}

		/* ---------- Masonry ---------- */

		layout() {
			const active = document.activeElement;
			const count = columnCount();
			this.removeClones();

			if (this.columns.length !== count) {
				this.columns.forEach((column) => column.remove());
				this.columns = Array.from({ length: count }, () => {
					const column = document.createElement('div');
					column.className = 'reviews-wall__col';
					this.grid.append(column);
					return column;
				});
			}

			// Park everything first so each column's height is built up card by card.
			this.cards.forEach((card) => this.stash.append(card));

			this.visible.forEach((card) => {
				let shortest = this.columns[0];
				for (const column of this.columns) {
					if (column.offsetHeight < shortest.offsetHeight) shortest = column;
				}
				shortest.append(card);
			});

			if (active && active !== document.body && this.root.contains(active) && document.activeElement !== active) {
				active.focus({ preventScroll: true });
			}
		}

		scheduleLayout() {
			if (this.frame) cancelAnimationFrame(this.frame);
			this.frame = requestAnimationFrame(() => {
				this.frame = null;
				this.layout();
				this.updateReadMore();
				if (this.isFlow) this.applyFlow();
			});
		}

		/* ---------- Read more ---------- */

		updateReadMore(cards = this.visible) {
			cards.forEach((card) => {
				const quote = card.querySelector(SELECTORS.quote);
				const read = card.querySelector(SELECTORS.read);
				if (!quote || !read) return;
				read.hidden = quote.scrollHeight - quote.clientHeight <= 1;
			});
		}

		/* ---------- Events ---------- */

		bindEvents() {
			this.filters.forEach((button) => {
				button.addEventListener('click', () => {
					if (this.filter === button.dataset.rwFilter) return;
					this.filter = button.dataset.rwFilter;
					this.flowBlocked = false;
					this.filters.forEach((pill) => pill.setAttribute('aria-pressed', String(pill === button)));
					this.update();
				});
			});

			if (this.moreButton) {
				this.moreButton.addEventListener('click', () => {
					const firstHidden = this.filteredCards()[this.visible.length];
					this.expanded = true;
					this.update();
					const target = firstHidden && firstHidden.querySelector(SELECTORS.open);
					if (target) target.focus();
				});
			}

			this.root.addEventListener('click', (event) => {
				const open = event.target.closest(SELECTORS.open);
				if (!open || !this.dialog || !supportsDialog) return;
				let card = open.closest(SELECTORS.card);
				// A flow clone stands in for its original card.
				if (card && card.hasAttribute('data-rw-clone')) {
					card = this.cards.find((original) => original.dataset.rwIndex === card.dataset.rwIndex);
				}
				const list = this.popupCards();
				const index = list.indexOf(card);
				if (index === -1) return;
				this.openedFrom = card.querySelector(SELECTORS.open);
				this.show(index);
				this.dialog.showModal();
				document.documentElement.classList.add('reviews-wall-lock');
				this.root.classList.add('is-dialog-open');
				this.dialog.querySelector(SELECTORS.close).focus();
			});

			// Card heights change once images and fonts arrive.
			this.cards.forEach((card) => {
				card.querySelectorAll('img').forEach((img) => {
					if (!img.complete) img.addEventListener('load', () => this.scheduleLayout(), { once: true });
				});
			});
			if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.scheduleLayout());
			window.addEventListener('load', () => this.scheduleLayout(), { once: true });

			// Column count or the mobile collapse can change on resize.
			let lastWidth = window.innerWidth;
			window.addEventListener('resize', () => {
				if (window.innerWidth === lastWidth) return;
				lastWidth = window.innerWidth;
				if (this.resizeTimer) clearTimeout(this.resizeTimer);
				this.resizeTimer = setTimeout(() => {
					this.flowBlocked = false;
					this.update();
				}, 100);
			});

			if (this.dialog && supportsDialog) this.bindDialog();
		}

		/* ---------- Review stream: flowing columns ---------- */

		setupFlow() {
			const root = this.root;
			this.speed = parseFloat(root.dataset.speed) || DEFAULT_FLOW_SPEED;

			this.toggle = root.querySelector(SELECTORS.toggle);
			if (this.toggle) {
				this.toggle.addEventListener('click', () => this.setPaused(!root.classList.contains('is-paused')));
			}

			// Nothing moves while the visitor can't see it.
			if ('IntersectionObserver' in window) {
				this.observer = new IntersectionObserver((entries) => {
					entries.forEach((entry) => root.classList.toggle('is-offscreen', !entry.isIntersecting));
				});
				this.observer.observe(this.grid);
			}

			// Focusing a card scrolls the window to show it; put the window back once focus
			// leaves so the columns line up with their loop again.
			this.grid.addEventListener('focusout', (event) => {
				if (this.grid.contains(event.relatedTarget)) return;
				this.grid.scrollTop = 0;
			});

			const onMotionChange = () => {
				this.flowBlocked = false;
				this.update();
			};
			if (reducedMotionQuery.addEventListener) reducedMotionQuery.addEventListener('change', onMotionChange);
			else if (reducedMotionQuery.addListener) reducedMotionQuery.addListener(onMotionChange);
		}

		setPaused(paused) {
			this.root.classList.toggle('is-paused', paused);
			if (!this.toggle) return;
			const label = paused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause;
			if (label) this.toggle.setAttribute('aria-label', label);
		}

		removeClones() {
			this.grid.querySelectorAll(SELECTORS.clone).forEach((clone) => clone.remove());
		}

		// Runs after every masonry layout. Clones each column's cards once and sets how
		// far (one full set) and how long it drifts before the loop starts over.
		applyFlow() {
			this.removeClones();
			this.columns.forEach((column) => {
				['--rw-shift', '--rw-duration', '--rw-delay'].forEach((name) => column.style.removeProperty(name));
			});

			if (!this.flowing) {
				this.grid.classList.remove('is-flowing');
				if (this.toggle) this.toggle.hidden = true;
				return;
			}

			this.grid.classList.add('is-flowing');
			const windowHeight = this.grid.clientHeight;
			const fills = this.columns.every((column) => column.children.length > 0 && column.offsetHeight > windowHeight);
			if (!fills) {
				// Too few cards for a seamless loop: stand still as the plain wall.
				this.grid.classList.remove('is-flowing');
				this.flowBlocked = true;
				this.update();
				return;
			}

			this.columns.forEach((column, index) => {
				const originals = Array.from(column.children);
				const clones = document.createDocumentFragment();
				originals.forEach((card) => {
					const clone = card.cloneNode(true);
					clone.setAttribute('data-rw-clone', '');
					clone.setAttribute('aria-hidden', 'true');
					clone.querySelectorAll('a, button').forEach((element) => element.setAttribute('tabindex', '-1'));
					clones.append(clone);
				});
				column.append(clones);

				// One full set of cards plus the gap before its first clone.
				const shift = column.children[originals.length].offsetTop - originals[0].offsetTop;
				const speed = this.speed * FLOW_SPEED_FACTORS[index % FLOW_SPEED_FACTORS.length];
				const duration = shift / speed;
				const delay = -FLOW_START_PX[index % FLOW_START_PX.length] / speed;
				column.style.setProperty('--rw-shift', `${shift}px`);
				column.style.setProperty('--rw-duration', `${duration.toFixed(2)}s`);
				column.style.setProperty('--rw-delay', `${delay.toFixed(2)}s`);
			});

			if (this.toggle) this.toggle.hidden = false;
		}

		/* ---------- Popup ---------- */

		bindDialog() {
			const dialog = this.dialog;
			dialog.querySelector(SELECTORS.close).addEventListener('click', () => dialog.close());
			dialog.querySelector(SELECTORS.prev).addEventListener('click', () => this.step(-1));
			dialog.querySelector(SELECTORS.next).addEventListener('click', () => this.step(1));

			// A click on the dark backdrop (outside the panel and its controls) closes it.
			dialog.addEventListener('click', (event) => {
				if (event.target === dialog || event.target.matches(SELECTORS.frame)) dialog.close();
			});

			dialog.addEventListener('keydown', (event) => {
				if (event.key === 'ArrowLeft') {
					event.preventDefault();
					this.step(-1);
				} else if (event.key === 'ArrowRight') {
					event.preventDefault();
					this.step(1);
				} else if (event.key === 'Tab') {
					this.trapFocus(event);
				}
			});

			// Esc closes natively; restore focus whichever way it closed.
			dialog.addEventListener('close', () => {
				document.documentElement.classList.remove('reviews-wall-lock');
				this.root.classList.remove('is-dialog-open');
				const card = this.popupCards()[this.current];
				const cardButton = card && card.querySelector(SELECTORS.open);
				const target = cardButton && cardButton.offsetParent !== null ? cardButton : this.openedFrom;
				if (target && document.contains(target)) target.focus();
				this.openedFrom = null;
				this.current = -1;
			});
		}

		trapFocus(event) {
			const focusable = Array.from(this.dialog.querySelectorAll(FOCUSABLE)).filter(
				(element) => !element.hidden && element.offsetParent !== null
			);
			if (!focusable.length) return;
			const first = focusable[0];
			const last = focusable[focusable.length - 1];
			if (event.shiftKey && (document.activeElement === first || !this.dialog.contains(document.activeElement))) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault();
				first.focus();
			}
		}

		step(delta) {
			const total = this.popupCards().length;
			if (total < 2 || this.current < 0) return;
			this.show((this.current + delta + total) % total);
		}

		show(index) {
			const dialog = this.dialog;
			const list = this.popupCards();
			const card = list[index];
			if (!card) return;
			this.current = index;

			const panel = dialog.querySelector(SELECTORS.panel);
			const media = dialog.querySelector(SELECTORS.panelMedia);
			const hasPhoto = Boolean(card.dataset.photoSrc);
			panel.classList.toggle('reviews-wall__panel--photo', hasPhoto);
			panel.classList.toggle('reviews-wall__panel--text', !hasPhoto);

			media.replaceChildren();
			media.hidden = !hasPhoto;
			if (hasPhoto) {
				const img = document.createElement('img');
				img.className = 'reviews-wall__panel-photo';
				img.src = card.dataset.photoSrc;
				if (card.dataset.photoSrcset) img.srcset = card.dataset.photoSrcset;
				img.sizes = '(min-width: 750px) 640px, 100vw';
				img.alt = card.dataset.photoAlt || '';
				// Never upscale past 1.5x the photo's natural size.
				const naturalWidth = parseInt(card.dataset.photoWidth, 10);
				const naturalHeight = parseInt(card.dataset.photoHeight, 10);
				if (naturalWidth > 0 && naturalHeight > 0) {
					img.style.setProperty('--rw-photo-max-w', `${Math.round(naturalWidth * PHOTO_MAX_SCALE)}px`);
					img.style.setProperty('--rw-photo-max-h', `${Math.round(naturalHeight * PHOTO_MAX_SCALE)}px`);
				}
				media.append(img);
			}

			const name = card.querySelector(SELECTORS.name);
			dialog.querySelector(SELECTORS.panelName).textContent = name ? name.textContent : '';

			const starsSlot = dialog.querySelector(SELECTORS.panelStars);
			starsSlot.replaceChildren();
			const stars = card.querySelector(SELECTORS.stars);
			if (stars) starsSlot.append(stars.cloneNode(true));

			const quoteSlot = dialog.querySelector(SELECTORS.panelQuote);
			quoteSlot.replaceChildren();
			const quote = card.querySelector(SELECTORS.quote);
			if (quote) quote.childNodes.forEach((node) => quoteSlot.append(node.cloneNode(true)));

			const product = dialog.querySelector(SELECTORS.panelProduct);
			const productUrl = card.dataset.productUrl;
			product.hidden = !productUrl;
			if (productUrl) {
				const thumbSlot = dialog.querySelector(SELECTORS.panelThumb);
				thumbSlot.replaceChildren();
				thumbSlot.hidden = !card.dataset.productThumb;
				if (card.dataset.productThumb) {
					const thumb = document.createElement('img');
					thumb.src = card.dataset.productThumb;
					thumb.alt = card.dataset.productTitle || '';
					thumb.width = 64;
					thumb.height = 64;
					thumbSlot.append(thumb);
				}
				dialog.querySelector(SELECTORS.panelProductTitle).textContent = card.dataset.productTitle || '';
				dialog.querySelector(SELECTORS.panelLink).href = productUrl;
			}

			const multiple = list.length > 1;
			dialog.querySelector(SELECTORS.prev).hidden = !multiple;
			dialog.querySelector(SELECTORS.next).hidden = !multiple;
			const counter = dialog.querySelector(SELECTORS.counter);
			counter.hidden = !multiple;
			counter.textContent = this.counterLabel.replace('[current]', index + 1).replace('[total]', list.length);

			dialog.querySelector(SELECTORS.panelText).scrollTop = 0;
			panel.scrollTop = 0;
		}
	}

	function init(root) {
		if (!root || root.dataset.rwInit === 'true') return;
		if (!root.querySelector(SELECTORS.grid)) return;
		root.dataset.rwInit = 'true';
		root.reviewsWall = new Wall(root);
	}

	function initAll(scope = document) {
		scope.querySelectorAll(SELECTORS.root).forEach(init);
	}

	window.ReviewsWall = { init, initAll };

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', () => initAll());
	} else {
		initAll();
	}

	// Theme editor: re-initialise when the section is re-rendered.
	document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
